/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const lastUpdatedDate = new Date(1785801600000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: null,
        description: null,
        lastUpdatedDate: "Tue Aug 04 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "zeta-global",
        type: "destination",
        icon: null,
        toc: [{
          "value": "Supported syncing",
          "depth": 0,
          "id": "supported-syncing"
        }, {
          "value": "Connect to Zeta Global",
          "depth": 0,
          "id": "connect-to-zeta-global"
        }, {
          "value": "Sync configuration",
          "depth": 0,
          "id": "sync-configuration"
        }, {
          "value": "Syncing contacts",
          "depth": 1,
          "id": "syncing-contacts"
        }, {
          "value": "Syncing unsubscribes",
          "depth": 1,
          "id": "syncing-unsubscribes"
        }, {
          "value": "Syncing events",
          "depth": 1,
          "id": "syncing-events"
        }, {
          "value": "Syncing audience lists",
          "depth": 1,
          "id": "syncing-audience-lists"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }, {
          "value": "Common errors",
          "depth": 1,
          "id": "common-errors"
        }, {
          "value": "Live debugger",
          "depth": 1,
          "id": "live-debugger"
        }, {
          "value": "Sync alerts",
          "depth": 1,
          "id": "sync-alerts"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      strong: "strong",
      p: "p",
      a: "a",
      ul: "ul",
      li: "li",
      h3: "h3",
      h4: "h4",
      code: "code"
    }, _provideComponents(), props.components), {NoCommonErrors, LiveDebugger, SyncAlerts} = _components;
    if (!LiveDebugger) _missingMdxReference("LiveDebugger", true);
    if (!NoCommonErrors) _missingMdxReference("NoCommonErrors", true);
    if (!SyncAlerts) _missingMdxReference("SyncAlerts", true);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "supported-syncing",
        children: "Supported syncing"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Type"
            }), _jsx(_components.th, {
              children: "Description"
            }), _jsx(_components.th, {
              children: "Supported Sync Modes"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Contacts"
              })
            }), _jsx(_components.td, {
              children: "Sync records to people in the Zeta Marketing Platform"
            }), _jsx(_components.td, {
              children: "Upsert"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Unsubscribes"
              })
            }), _jsx(_components.td, {
              children: "Unsubscribe a person's email address or phone number in Zeta"
            }), _jsx(_components.td, {
              children: "Insert"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Events"
              })
            }), _jsx(_components.td, {
              children: "Sync records as events on a person's Zeta profile"
            }), _jsx(_components.td, {
              children: "Insert"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Audience lists"
              })
            }), _jsx(_components.td, {
              children: "Add and remove people from an existing audience list in Zeta"
            }), _jsx(_components.td, {
              children: "Add, Remove"
            })]
          })]
        })]
      }), "\n", _jsxs(_components.p, {
        children: ["For more information about sync modes, refer to the ", _jsx(_components.a, {
          href: "/syncs/types-and-modes#sync-modes",
          children: "sync modes"
        }), " docs."]
      }), "\n", _jsx(_components.h2, {
        id: "connect-to-zeta-global",
        children: "Connect to Zeta Global"
      }), "\n", _jsxs(_components.p, {
        children: ["Go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/destinations?from=docs",
          children: [_jsx(_components.strong, {
            children: "Destinations"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add destination"
        }), " button. Select ", _jsx(_components.strong, {
          children: "Zeta Global"
        }), " and click ", _jsx(_components.strong, {
          children: "Continue"
        }), ". You can then authenticate Hightouch to ", _jsx(_components.strong, {
          children: "Zeta Global"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: "Enter the following fields into Hightouch:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Site ID"
          }), ": The site ID of your Zeta Marketing Platform account."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "REST API key"
          }), ": A REST API key generated in Zeta under ", _jsx(_components.strong, {
            children: "Settings"
          }), " > ", _jsx(_components.strong, {
            children: "Integrations"
          }), " > ", _jsx(_components.strong, {
            children: "Keys & Apps"
          }), ". Refer to the ", _jsx(_components.a, {
            href: "https://docs.zetaglobal.com/reference/getting-started",
            children: "Zeta documentation"
          }), " for more information."]
        }), "\n"]
      }), "\n", _jsx(_components.h2, {
        id: "sync-configuration",
        children: "Sync configuration"
      }), "\n", _jsxs(_components.p, {
        children: ["Once you've set up your Zeta Global destination and have a ", _jsx(_components.a, {
          href: "/getting-started/concepts#models",
          children: "model"
        }), " to pull data from, you can set up your sync configuration to begin syncing data. Go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/syncs?from=docs",
          children: [_jsx(_components.strong, {
            children: "Syncs"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add sync"
        }), " button to begin. Then, select the relevant model and the Zeta Global destination you want to sync to."]
      }), "\n", _jsx(_components.h3, {
        id: "syncing-contacts",
        children: "Syncing contacts"
      }), "\n", _jsxs(_components.p, {
        children: ["Sync records to people in Zeta using Zeta's ", _jsx(_components.a, {
          href: "https://docs.zetaglobal.com/reference/identify-a-user",
          children: "identify endpoint"
        }), ". Hightouch creates the person if they don't exist yet and updates them if they do."]
      }), "\n", _jsx(_components.h4, {
        id: "record-matching",
        children: "Record matching"
      }), "\n", _jsxs(_components.p, {
        children: ["To match rows from your model to people in Zeta, you need to select a model column that maps to the person's ", _jsx(_components.strong, {
          children: "User ID"
        }), ". This should be the unique ID you use for the person in your own systems."]
      }), "\n", _jsx(_components.h4, {
        id: "field-mapping",
        children: "Field mapping"
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch lets you sync person fields via ", _jsx(_components.a, {
          href: "/syncs/mapping-data",
          children: "field mapping"
        }), ". You can map data from any of your model columns to the following Zeta fields:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "First name"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "Last name"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "Full name"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "Email"
          })
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Phone"
          }), ": Phone numbers must be in E.164 format with a leading \"+\" symbol."]
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "Signed up at"
          })
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Subscription status"
          }), ": Either ", _jsx(_components.code, {
            children: "active"
          }), " or ", _jsx(_components.code, {
            children: "inactive"
          }), ". When mapped, the status applies to the synced email and phone contacts."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "Email and phone values are synced as entries in the person's contacts in Zeta. You can also map any of your model columns to custom attributes on the person's Zeta profile."
      }), "\n", _jsx(_components.h3, {
        id: "syncing-unsubscribes",
        children: "Syncing unsubscribes"
      }), "\n", _jsxs(_components.p, {
        children: ["Unsubscribe a person's email address or phone number in Zeta using Zeta's ", _jsx(_components.a, {
          href: "https://docs.zetaglobal.com/reference/unsubscribe-a-contact",
          children: "unsubscribe endpoint"
        }), ". This lets you manage subscription statuses from an external preference center while Zeta attributes the unsubscribe correctly."]
      }), "\n", _jsx(_components.p, {
        children: "Each new row added to your model unsubscribes one of the person's contacts. Zeta doesn't have an API to resubscribe a contact, so this sync only processes added rows."
      }), "\n", _jsx(_components.h4, {
        id: "record-matching-1",
        children: "Record matching"
      }), "\n", _jsxs(_components.p, {
        children: ["To match rows from your model to people in Zeta, you need to select a model column that maps to the person's ", _jsx(_components.strong, {
          children: "User ID"
        }), "."]
      }), "\n", _jsx(_components.h4, {
        id: "field-mapping-1",
        children: "Field mapping"
      }), "\n", _jsx(_components.p, {
        children: "You must map the following fields:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Contact value"
          }), ": The email address or phone number to unsubscribe."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Contact type"
          }), ": Either ", _jsx(_components.code, {
            children: "email"
          }), " or ", _jsx(_components.code, {
            children: "phone"
          }), ". If all rows target the same channel, you can use a static value instead of a model column."]
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "syncing-events",
        children: "Syncing events"
      }), "\n", _jsxs(_components.p, {
        children: ["Sync records as events on a person's Zeta profile using Zeta's ", _jsx(_components.a, {
          href: "https://docs.zetaglobal.com/reference/track-an-event-rest",
          children: "event tracking endpoint"
        }), "."]
      }), "\n", _jsxs(_components.p, {
        children: ["When configuring the sync, you must enter an event name and select a model column that maps to the person's ", _jsx(_components.strong, {
          children: "User ID"
        }), ". You can optionally select a timestamp column; if you don't, Zeta defaults the event's timestamp to the time it receives the event. Any other columns you map are synced as the event's properties."]
      }), "\n", _jsx(_components.h3, {
        id: "syncing-audience-lists",
        children: "Syncing audience lists"
      }), "\n", _jsxs(_components.p, {
        children: ["Add and remove people from an existing audience list in Zeta using Zeta's ", _jsx(_components.a, {
          href: "https://docs.zetaglobal.com/reference/add-or-remove-people-from-audience-lists-bulk",
          children: "bulk audience list endpoint"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: "When configuring the sync, select the audience list to sync to. Hightouch lists your Zeta account's static segments to choose from. To create a new list, use the Zeta Marketing Platform directly."
      }), "\n", _jsx(_components.h4, {
        id: "record-matching-2",
        children: "Record matching"
      }), "\n", _jsxs(_components.p, {
        children: ["To match rows from your model to people in Zeta, you need to select a model column that maps to the person's ", _jsx(_components.strong, {
          children: "User ID or email"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: "Rows added to your model are added to the audience list; Zeta creates people that don't exist yet on add. When rows leave your model, you can choose whether Hightouch removes them from the audience list or does nothing."
      }), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", _jsx(_components.h3, {
        id: "common-errors",
        children: "Common errors"
      }), "\n", _jsx(NoCommonErrors, {}), "\n", _jsx(_components.h3, {
        id: "live-debugger",
        children: "Live debugger"
      }), "\n", _jsx(LiveDebugger, {}), "\n", _jsx(_components.h3, {
        id: "sync-alerts",
        children: "Sync alerts"
      }), "\n", _jsx(SyncAlerts, {})]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
